import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { getToken } from '../auth/PasswordGate';

export interface CategoryRule {
  id: number;
  scope: 'user' | 'household';
  merchantPattern: string;
  budgetCategory: string;
  /** Absolute dollar amount the rule is restricted to, or null when it
   *  matches any amount. */
  amount: number | null;
  createdAt: string;
  updatedAt: string;
  isMine: boolean;
}

export interface UpdateRuleInput {
  merchantPattern?: string;
  budgetCategory?: string;
  /** Pass a positive number to limit the rule to that exact amount,
   *  `null` to clear the constraint, or omit the field to leave it
   *  unchanged. */
  amount?: number | null;
  /** When true, also recategorize past matching transactions whose
   *  is_categorized_manually = FALSE. */
  backfill?: boolean;
}

export interface UpdateRuleResult {
  /** Number of historical transactions recategorized as a result of this edit. */
  backfilled: number;
}

export type RulesState =
  | { status: 'loading'; rules: [] }
  | {
      status: 'ready';
      rules: CategoryRule[];
      reload: () => void;
      remove: (id: number) => Promise<void>;
      update: (id: number, input: UpdateRuleInput) => Promise<UpdateRuleResult>;
    }
  | { status: 'error'; rules: []; error: string; reload: () => void };

export function useRules(): RulesState {
  const [state, setState] = useState<RulesState>({ status: 'loading', rules: [] });

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setState({
        status: 'ready',
        rules: [],
        reload: load,
        remove: async () => {},
        update: async () => ({ backfilled: 0 }),
      });
      return;
    }
    apiFetch('/api/rules')
      .then(async (res) => {
        if (!res.ok) throw new Error(`rules ${res.status}`);
        return (await res.json()) as { rules: CategoryRule[] };
      })
      .then((data) => {
        setState({
          status: 'ready',
          rules: data.rules,
          reload: load,
          remove: async (id: number) => {
            const res = await apiFetch('/api/rules', {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ruleId: id }),
            });
            if (!res.ok) {
              const body = await res.text().catch(() => '');
              throw new Error(`delete ${res.status}: ${body.slice(0, 200)}`);
            }
            load();
          },
          update: async (id, input) => {
            const res = await apiFetch('/api/rules', {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ruleId: id, ...input }),
            });
            if (!res.ok) {
              const body = await res.text().catch(() => '');
              throw new Error(`update ${res.status}: ${body.slice(0, 200)}`);
            }
            const out = (await res.json()) as { backfilled: number };
            load();
            return { backfilled: out.backfilled ?? 0 };
          },
        });
      })
      .catch((err: Error) => {
        setState({ status: 'error', rules: [], error: err.message, reload: load });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return state;
}
